import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link, useParams, useHistory, useLocation } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { FileText, Loader2, Send, XCircle, ExternalLink, Pencil } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Alert, AlertDescription } from '../../ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import Breadcrumb from '../../Common/Breadcrumb';
import ConsultantPricingSchedule from '../../Agreements/ConsultantPricingSchedule';
import { Actions as AGREEMENTS } from '../../../store/actions/agreementsActions';
import { toggleAlertMessage } from '../../../store/action';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../ui/dialog';
import { isEditableAgreementStatus } from './agreementFormUtils';

const resolveCustomerOrgId = (location, listOrganisationId) => {
  const fromQuery = new URLSearchParams(location.search).get('org');
  if (fromQuery) return String(fromQuery);
  if (listOrganisationId) return String(listOrganisationId);
  return '';
};

const AgreementDetail = ({
  getMyAgreements,
  resendAgreement,
  voidAgreement,
  getAgreementSignedPdfUrl,
  list,
  listOrganisationId,
  loading,
  showMessage,
}) => {
  const { id: routeId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const customerOrgId = resolveCustomerOrgId(location, listOrganisationId);
  const [voidOpen, setVoidOpen] = React.useState(false);
  const [voidReason, setVoidReason] = React.useState('');
  const [rotateToken, setRotateToken] = React.useState(false);
  const [loadAttempted, setLoadAttempted] = React.useState(false);

  useEffect(() => {
    if (!customerOrgId) {
      setLoadAttempted(true);
      return;
    }
    (async () => {
      await getMyAgreements(customerOrgId);
      setLoadAttempted(true);
    })();
  }, [customerOrgId, getMyAgreements]);

  const c = useMemo(
    () => (list || []).find((x) => x.id === routeId),
    [list, routeId]
  );

  const canEdit = c && isEditableAgreementStatus(c.status);
  const editHref =
    customerOrgId && routeId
      ? `/admin/agreements/${encodeURIComponent(routeId)}/edit?org=${encodeURIComponent(customerOrgId)}`
      : null;

  const onResend = async () => {
    const r = await resendAgreement(routeId, { rotateToken }, customerOrgId);
    if (r?.ok) {
      showMessage(true, r.data?.agreementUrl ? 'Invite re-sent' : 'Resent', 'success', 3000);
    } else {
      showMessage(true, r?.message || 'Resend failed', 'error', 5000);
    }
  };

  const onVoid = async () => {
    const r = await voidAgreement(routeId, { reason: voidReason }, customerOrgId);
    if (r?.ok) {
      setVoidOpen(false);
      showMessage(true, 'Agreement voided', 'success', 3000);
      history.push(
        customerOrgId
          ? `/admin/agreements?org=${encodeURIComponent(customerOrgId)}`
          : '/admin/agreements'
      );
    } else {
      showMessage(true, r?.message || 'Void failed', 'error', 5000);
    }
  };

  const onPdf = async () => {
    const result = await getAgreementSignedPdfUrl(routeId, customerOrgId);
    const url = typeof result === 'string' ? result : result?.url;
    if (url) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    const code = typeof result === 'object' ? result?.error : null;
    const message =
      code === 'pdf_not_available'
        ? 'No order-form PDF is stored on this agreement yet (create/accept may have skipped PDF generation).'
        : code === 'forbidden'
          ? 'Not authorised to download this PDF.'
          : 'PDF not available';
    showMessage(true, message, 'error', 5000);
  };

  if (!customerOrgId) {
    return (
      <div className="p-6 max-w-lg space-y-3">
        <p>
          Open this agreement from the list after selecting the customer organisation, or add{' '}
          <code>?org=&lt;organisationId&gt;</code> to the URL.
        </p>
        <Button onClick={() => history.push('/admin/agreements')}>Back to agreements</Button>
      </div>
    );
  }

  if ((loading || !loadAttempted) && !c) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="h-10 w-10 animate-spin" />
      </div>
    );
  }

  if (!c) {
    return (
      <div className="p-6 max-w-lg space-y-3">
        <p>
          Agreement not found for this organisation. Confirm the organisation filter on the list
          matches the customer for this contract, then try again.
        </p>
        <Button onClick={() => history.push('/admin/agreements')}>Back to agreements</Button>
      </div>
    );
  }

  const canVoid = !['voided', 'expired', 'complete', 'accepted'].includes(c.status);

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <Breadcrumb
        items={[
          { label: 'Agreements', link: '/admin/agreements' },
          { label: c.customerOrganisationName || c.id },
        ]}
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText size={20} strokeWidth={1.5} />
            {c.type} — {c.status}
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            {canEdit && editHref && (
              <Button variant="default" size="sm" asChild>
                <Link to={editHref}>
                  <Pencil size={16} strokeWidth={1.5} className="mr-1" />
                  Edit
                </Link>
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onPdf}>
              <ExternalLink size={16} strokeWidth={1.5} className="mr-1" />
              PDF
            </Button>
            {canVoid && (
              <Button variant="secondary" size="sm" onClick={onResend}>
                <Send size={16} strokeWidth={1.5} className="mr-1" />
                Resend
              </Button>
            )}
            {canVoid && (
              <Button variant="destructive" size="sm" onClick={() => setVoidOpen(true)}>
                <XCircle size={16} strokeWidth={1.5} className="mr-1" />
                Void
              </Button>
            )}
            <Button variant="outline" size="sm" asChild>
              <Link
                to={
                  '/admin/agreements/' +
                  encodeURIComponent(c.id) +
                  '/indexation' +
                  (customerOrgId ? '?org=' + encodeURIComponent(customerOrgId) : '')
                }
              >
                Indexation
              </Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          {canEdit && (
            <Alert className="mb-2">
              <AlertDescription className="text-xs">
                This agreement has not been signed yet. Use <strong>Edit</strong> to revise the
                order form and send a new invite (the current version will be voided).
              </AlertDescription>
            </Alert>
          )}
          {canVoid && (
            <div className="flex items-center gap-2 mb-2">
              <Checkbox id="rot" checked={rotateToken} onCheckedChange={setRotateToken} />
              <Label htmlFor="rot">Rotate token on resend</Label>
            </div>
          )}
          {c.acceptedAt && (
            <p>
              <span className="text-muted-foreground">Accepted: </span>
              {format(parseISO(c.acceptedAt), 'PPP p')}
            </p>
          )}
          {c.term && (
            <p>
              <span className="text-muted-foreground">Term: </span>
              {c.term.commitmentEndDate
                ? `ends ${format(parseISO(c.term.commitmentEndDate), 'PPP')}`
                : JSON.stringify(c.term)}
            </p>
          )}
        </CardContent>
      </Card>

      {c.lineItems && c.lineItems.length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle>Line items</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {c.lineItems.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.description}</TableCell>
                    <TableCell>{r.kind}</TableCell>
                    <TableCell className="text-right">{r.quantity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {c.consultantPricingSchedule && (
        <div className="mt-4">
          <ConsultantPricingSchedule schedule={c.consultantPricingSchedule} />
          <p className="mt-2 text-xs text-muted-foreground">
            This is the schedule snapshotted on this agreement.{' '}
            <Link to="/admin/sales/catalog" className="text-primary underline-offset-2 hover:underline">
              View live catalog
            </Link>
            .
          </p>
        </div>
      )}

      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Void agreement</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Label>Reason (optional)</Label>
            <Input value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={onVoid}>
              Confirm void
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const mapState = (s) => ({
  list: s.agreements?.list || [],
  listOrganisationId: s.agreements?.listOrganisationId,
  loading: s.agreements?.loading?.list,
});

const mapDispatch = (d) => ({
  getMyAgreements: (oid) => d(AGREEMENTS.getMyAgreements(oid)),
  resendAgreement: (id, body, oid) => d(AGREEMENTS.resendAgreement(id, body, oid)),
  voidAgreement: (id, body, oid) => d(AGREEMENTS.voidAgreement(id, body, oid)),
  getAgreementSignedPdfUrl: (id, oid) => d(AGREEMENTS.getAgreementSignedPdfUrl(id, oid)),
  showMessage: (...a) => d(toggleAlertMessage(...a)),
});

export default connect(mapState, mapDispatch)(AgreementDetail);
